import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
	ArrowLeft,
	Lock,
	Shield,
	Database,
	UserCheck,
	CheckCircle2,
	Share2,
	Clock3,
	Settings2,
	FileWarning,
	Mail,
} from 'lucide-react';
import Button from './ui/Button';

const PrivacyPolicy: React.FC = () => {
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<div className="bg-white shadow-sm">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
					<div className="flex items-center gap-4">
						<button
							onClick={() => navigate('/verify')}
							className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
						>
							<ArrowLeft className="w-4 h-4 text-gray-600" />
						</button>
						<div className="flex items-center gap-2">
							<Lock className="w-4 h-4 text-brand-orange" />
							<h1 className="text-xl font-bold text-gray-900">Privacy Policy</h1>
						</div>
					</div>
				</div>
			</div>

			<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
				<div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
					{/* Last Updated */}
					<div className="mb-4 p-4 bg-blue-50 rounded-lg">
						<p className="text-body text-blue-700">
							<strong>Last Updated:</strong> July 2025
						</p>
					</div>

					{/* Intro */}
					<section className="mb-6">
						<h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
							<Shield className="w-4 h-4 text-brand-orange" />
							Surge Microfinance Limited Privacy Statement
						</h3>
						<p className="text-body text-gray-700 leading-relaxed">
							Surge Microfinance Limited is committed to protecting your privacy and ensuring the
							lawful processing of your personal data in accordance with the Data Protection Act,
							2012 (Act 843) of the Republic of Ghana. This Privacy Policy outlines how we collect,
							use, disclose, and safeguard your personal information when you access and use the
							Surge NIA Verification Portal.
							<br />
							<br />
							Please read this policy carefully. By proceeding with the identity verification
							process, you consent to the collection and use of your personal data as described
							herein.
						</p>
					</section>

					{/* 1. Personal Data We Collect */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Database className="w-4 h-4 text-brand-orange" />
							1. Personal Data We Collect
						</h2>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<p className="text-gray-700 mb-2">
								Through the NIA Verification Portal, we may collect and process the following
								categories of personal data:
							</p>
							<li>
								<strong>Biometric Data:</strong> Live selfie/photo for facial recognition.
							</li>
							<li>
								<strong>National Identification Details:</strong> Ghana Card number, full name, date
								of birth, and other data as issued by the NIA.
							</li>
							<li>
								<strong>Contact Information:</strong> Phone number, email address (if provided).
							</li>
							<li>
								<strong>Device/Technical Data:</strong> IP address, browser type, access time, and
								usage logs for security monitoring.
							</li>
						</ul>
					</section>

					{/* 2. Purpose of Data Collection */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<UserCheck className="w-4 h-4 text-brand-orange" />
							2. Purpose of Data Collection
						</h2>
						<p className="text-gray-700 mb-2">
							We collect your data for the following lawful and legitimate purposes:
						</p>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<li>
								To verify your identity with the National Identification Authority (NIA) as part of
								our onboarding process.
							</li>
							<li>To comply with legal and regulatory requirements.</li>
							<li>To ensure the accuracy and authenticity of information provided by you.</li>
							<li>
								To safeguard our systems and protect against identity theft, fraud, and financial
								crime.
							</li>
						</ul>
					</section>

					{/* 3. Lawful Basis */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<CheckCircle2 className="w-4 h-4 text-brand-orange" />
							3. Lawful Basis for Processing
						</h2>
						<p className="text-gray-700 mb-2">
							We rely on the following lawful bases under Act 843:
						</p>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<li>
								<strong>Consent:</strong> Your explicit acceptance of this privacy policy prior to
								verification.
							</li>
							<li>
								<strong>Legal Obligation:</strong> Compliance with applicable laws and regulations.
							</li>
							<li>
								<strong>Legitimate Interest:</strong> To validate identities and ensure the
								integrity of our lending operations.
							</li>
						</ul>
					</section>

					{/* 4. Data Sharing */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Share2 className="w-4 h-4 text-brand-orange" />
							4. How We Share Your Data
						</h2>
						<p className="text-gray-700 mb-2">
							We do not sell or rent your personal data. However, we may share your data in the
							following situations:
						</p>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<li>
								With the National Identification Authority (NIA) for the sole purpose of identity
								verification.
							</li>
							<li>
								With our technology service providers who support the functionality of the
								verification portal under strict confidentiality agreements.
							</li>
							<li>
								Where required by law enforcement or regulatory authorities in accordance with the
								law.
							</li>
						</ul>
					</section>

					{/* 5. Retention */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Clock3 className="w-4 h-4 text-brand-orange" />
							5. Data Retention
						</h2>
						<p className="text-body text-gray-700 mb-2">
							We retain your personal data only for as long as necessary for the purpose it was
							collected, or as required by law.
						</p>
					</section>

					{/* 6. Security */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Shield className="w-4 h-4 text-brand-orange" />
							6. Data Security
						</h2>
						<p className="text-body text-gray-700 mb-2">
							We have implemented appropriate technical and organizational security measures to
							protect your personal data from unauthorized access, disclosure, alteration, or
							destruction. This includes:
						</p>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<li>Encryption</li>
							<li>Firewalls</li>
							<li>Access controls</li>
							<li>Secure data storage</li>
						</ul>
					</section>

					{/* 7. Your Rights */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Settings2 className="w-4 h-4 text-brand-orange" />
							7. Your Rights as a Data Subject
						</h2>
						<p className="text-gray-700 mb-2">
							In accordance with the Data Protection Act, 2012 (Act 843), you have the right to:
						</p>
						<ul className="list-disc list-inside text-body text-gray-700 space-y-2">
							<li>Request access to your personal data.</li>
							<li>Request correction of inaccurate data.</li>
							<li>
								Withdraw your consent at any time (note: this may affect your ability to complete
								verification).
							</li>
							<li>Object to or restrict certain types of processing.</li>
							<li>
								Lodge a complaint with the Data Protection Commission if you believe your data
								rights have been violated.
							</li>
						</ul>
					</section>

					{/* 8. Changes */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<FileWarning className="w-4 h-4 text-brand-orange" />
							8. Changes to This Policy
						</h2>
						<p className="text-body text-gray-700 mb-2">
							We may update this privacy policy from time to time to reflect changes in technology,
							legal requirements, or our operations. The updated version will be made available on
							the portal with the effective date clearly indicated.
						</p>
					</section>

					{/* 9. Contact */}
					<section className="mb-6">
						<h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
							<Mail className="w-4 h-4 text-brand-orange" />
							9. Contact Us
						</h2>
						<div className="bg-gray-50 rounded-lg p-4 text-body text-gray-700">
							<p className="text-gray-700 mb-2">
								If you have any questions, concerns, or complaints regarding this policy or your
								personal data, please contact:
							</p>
							<p className="mt-2">
								<strong>General Inquiries:</strong> info@surge.africa
								<br />
								<strong>Phone:</strong> +233 53 100 7179
							</p>
						</div>
					</section>

					{/* Back Button */}
					<div className="pt-8 border-t border-gray-200">
						<Button
							onClick={() => navigate('/verify')}
							className="px-6 py-3 bg-brand-orange text-white rounded-lg hover:bg-red-700 transition-colors"
						>
							Back to Verification
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrivacyPolicy;
